import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation, Trans } from 'react-i18next';
import SliderDialog from 'components/global/SliderDialog/SliderDialog';
import { Accordion } from 'components/global/Accordion/Accordion';
import clickURLIconImage from 'components/global/assets/images/video-question-troubleshoot-1.png';
import enablePermissionsImage from 'components/global/assets/images/video-question-troubleshoot-2.png';

export const TroubleshootingVideoRecord = () => {
  const { t } = useTranslation();
  const [isSliderOpen, setIsSliderOpen] = useState(false);

  const handleOpenSlider = () => {
    setIsSliderOpen(true);
  };

  const handleCloseSlider = () => {
    setIsSliderOpen(false);
  };

  const renderEnableCameraAccordion = () => {
    const content = (
      <div>
        <StyledText
          dangerouslySetInnerHTML={{ __html: t('troubleshootingPanel.step1') }}
        />
        <ImageContainer>
          <img
            src={clickURLIconImage}
            alt="Click icon next to the URL in your browser"
          />
        </ImageContainer>
        <StyledText
          dangerouslySetInnerHTML={{ __html: t('troubleshootingPanel.step2') }}
        />
        <ImageContainer>
          <img src={enablePermissionsImage} alt="Enable permissions" />
        </ImageContainer>
        <StyledText>{t('troubleshootingPanel.step3')}</StyledText>
      </div>
    );
    return (
      <div>
        <Accordion
          title={t('troubleshootingPanel.howToEnableTitle')}
          content={content}
        />
      </div>
    );
  };

  const renderFixPermissionsAccordion = () => {
    const videoDemoLink =
      'https://www.loom.com/share/e99563bfda6c47e1bc622352d752aefc?sid=a0678b3d-a98b-4ba3-a4b5-8b8f5eba8897';
    const content = (
      <div>
        <StyledText>
          <Trans
            i18nKey="troubleshootingPanel.videoDemo"
            components={{
              1: (
                <a
                  href={videoDemoLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#0059B8' }}
                  aria-label={t('troubleshootingPanel.videoDemoLinkAriaLabel')}
                />
              )
            }}
          />
        </StyledText>
        <StyledOrderedList>
          <li>{t('troubleshootingPanel.openBrowserSettings')}</li>
          <li
            dangerouslySetInnerHTML={{
              __html: t('troubleshootingPanel.findCameraSettings')
            }}
          />
          <li
            dangerouslySetInnerHTML={{
              __html: t('troubleshootingPanel.ensureEnabled')
            }}
          />
          <li>{t('troubleshootingPanel.checkNotBlocked')}</li>
          <li
            dangerouslySetInnerHTML={{
              __html: t('troubleshootingPanel.refreshAfterChanges')
            }}
          />
        </StyledOrderedList>
      </div>
    );
    return (
      <div>
        <Accordion
          title={t('troubleshootingPanel.howToFixPermissionsTitle')}
          content={content}
        />
      </div>
    );
  };

  const renderContactSupportAccordion = () => {
    const content = (
      <div>
        <StyledText>{t('troubleshootingPanel.stillHavingIssues')}</StyledText>
        <StyledUnorderedList>
          <li>{t('troubleshootingPanel.briefDescription')}</li>
          <li>{t('troubleshootingPanel.assessmentName')}</li>
          <li>{t('troubleshootingPanel.stepsTaken')}</li>
          <li>{t('troubleshootingPanel.timeOccurred')}</li>
        </StyledUnorderedList>
      </div>
    );
    return (
      <div>
        <Accordion
          title={t('troubleshootingPanel.needMoreHelpTitle')}
          content={content}
        />
      </div>
    );
  };

  const renderFinalStepToTryAccordion = () => {
    const content = (
      <div>
        <StyledText
          dangerouslySetInnerHTML={{
            __html: t('troubleshootingPanel.restartLaptop')
          }}
        />
        <StyledText>{t('troubleshootingPanel.ifFixedContinue')}</StyledText>
      </div>
    );
    return (
      <div>
        <Accordion
          title={t('troubleshootingPanel.finalStepTitle')}
          content={content}
        />
      </div>
    );
  };

  const renderSliderContent = () => {
    return (
      <div>
        <h3>{t('troubleshootingPanel.cameraOrMicTroubleshootingTitle')}</h3>
        <StyledNoteText>
          <Trans
            i18nKey="troubleshootingPanel.chromeNote"
            components={{
              1: (
                <a
                  href="https://www.google.com/chrome/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#0059B8', textDecoration: 'none' }}
                  aria-label={t('troubleshootingPanel.chromeForPcAriaLabel')}
                />
              ),
              2: <span />,
              3: (
                <a
                  href="https://www.google.com/chrome/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#0059B8', textDecoration: 'none' }}
                  aria-label={t('troubleshootingPanel.chromeForMacAriaLabel')}
                />
              )
            }}
          />
          <br /> <br />
          {t('troubleshootingPanel.otherBrowserNote')}
        </StyledNoteText>
        {renderEnableCameraAccordion()}
        {renderFixPermissionsAccordion()}
        {renderFinalStepToTryAccordion()}
        {renderContactSupportAccordion()}
      </div>
    );
  };

  return (
    <>
      <p>
        <b>{t('videoQuestion.troubleshootingLink').split(':')[0]}: </b>
        <span
          onClick={handleOpenSlider}
          style={{
            color: '#0059B8',
            cursor: 'pointer'
          }}
        >
          {t('videoQuestion.troubleshootingLink').split(': ')[1]}
        </span>
      </p>

      <SliderDialog
        isOpen={isSliderOpen}
        onClose={handleCloseSlider}
        title={t('troubleshootingPanel.cameraOrMicTroubleshootingTitle')}
        slideDistance="720px"
        right={0}
        zIndex={1000}
      >
        {renderSliderContent()}
      </SliderDialog>
    </>
  );
};

const baseTextStyle = `
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  margin: 0px;
  padding: 16px 0px;
`;

const StyledNoteText = styled.p`
  ${baseTextStyle}
  margin-bottom: 10px;
`;

const StyledText = styled.p`
  ${baseTextStyle}
`;

const StyledOrderedList = styled.ol`
  ${baseTextStyle}
  margin: 0;
  padding: 0 0 0 16px;
`;

const StyledUnorderedList = styled.ul`
  ${baseTextStyle}
  margin: 0;
  padding: 0 0 0 16px;
`;

const ImageContainer = styled.div`
  padding: 16px 32px;
`;
